import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { BookmarkPlus, X, Sparkles, Trash2, ArrowRight } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api } from "@/lib/api";

export default function PromptLibrary({ open, onOpenChange, projectId, onApply, refreshKey = 0 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/prompts?project_id=${projectId}`)
      .then(r => setItems(r.data.prompts))
      .finally(() => setLoading(false));
  }, [open, projectId, refreshKey]);

  const remove = async (id) => {
    try {
      await api.delete(`/prompts/${id}`);
      setItems((p) => p.filter(x => x.id !== id));
      toast.success("Removed from library");
    } catch { toast.error("Failed to remove"); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="bg-[#0E0E0E] border-zinc-800 text-[#F5F5F7] w-full sm:max-w-md p-0 overflow-hidden flex flex-col" data-testid="prompt-library-sheet">
        <SheetHeader className="px-6 pt-5 pb-4 border-b border-zinc-800">
          <SheetTitle className="font-heading text-xl flex items-center gap-2">
            <Sparkles className="w-5 h-5" style={{color:'#5FC0C8'}}/> Prompt Library
          </SheetTitle>
          <p className="text-sm text-zinc-500">Tap any prompt to load it back into the workspace.</p>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {loading ? (
            <div className="text-zinc-500 text-sm font-mono px-2">loading…</div>
          ) : items.length === 0 ? (
            <div className="ax-card p-8 text-center" data-testid="prompt-library-empty">
              <BookmarkPlus className="w-6 h-6 mx-auto text-zinc-600"/>
              <p className="text-zinc-500 text-sm mt-3">No saved prompts yet. Save one from any generation.</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {items.map(p => (
                <div key={p.id} className="ax-card p-4 group" data-testid={`prompt-library-item-${p.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest">
                        {p.model_id || p.category || "prompt"}
                      </div>
                      <p className="text-sm text-zinc-200 mt-1 leading-relaxed line-clamp-4">{p.prompt}</p>
                    </div>
                    <button
                      onClick={()=>remove(p.id)}
                      data-testid={`prompt-library-delete-${p.id}`}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-500 hover:text-red-400"
                    ><Trash2 className="w-4 h-4"/></button>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-[10px] font-mono text-zinc-600 uppercase tracking-widest">
                      {new Date(p.created_at).toLocaleDateString()}
                    </span>
                    <button
                      onClick={()=>{ onApply(p); onOpenChange(false); }}
                      data-testid={`prompt-library-use-${p.id}`}
                      className="text-xs font-mono text-[#5FC0C8] hover:text-white uppercase tracking-widest flex items-center gap-1"
                    >Use <ArrowRight className="w-3 h-3"/></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
